/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Dispatch, SetStateAction } from 'react'
import { languages, IActivityName, ICollapsedLangs, IRoleName, ICategoryName } from './constants'
import { CreateActivityRequestPossibleRolesInner, LocalizedName } from '../services/network'
import { useToast } from '@chakra-ui/react'

const updateAllLanguages = (
  langCode: string,
  value: string,
  currentValues: Record<string, string>,
  setValues: Dispatch<SetStateAction<IRoleName>>,
  showTranslateMenu: boolean
): boolean => {
  if (langCode === 'it' && !showTranslateMenu) {
    if (Object.values(currentValues).every((val, _, arr) => val === arr[0])) {
      const updatedValues = { ...currentValues }
      languages.forEach((lang) => {
        updatedValues[lang.code] = value
      })
      setValues(updatedValues)
      return true // Indicates that all values were updated
    }
  }
  return false // Indicates that individual value update should proceed
}

export const handleRoleNameChange = (
  langCode: string,
  value: string,
  roleNames: IRoleName,
  setRoleNames: Dispatch<SetStateAction<IRoleName>>,
  setCurrentRoleValue: Dispatch<SetStateAction<IRoleName>>,
  showTranslateMenu: boolean
): void => {
  const allUpdated = updateAllLanguages(langCode, value, roleNames, setRoleNames, showTranslateMenu)
  if (!allUpdated) {
    setCurrentRoleValue({ ...roleNames, [langCode]: value })
  }
}

export const handlePossibleRolesChange = (
  possibleRoles: CreateActivityRequestPossibleRolesInner[],
  roleNames: IRoleName,
  setPossibleRoles: Dispatch<SetStateAction<CreateActivityRequestPossibleRolesInner[]>>,
  setRoleNames: Dispatch<SetStateAction<IRoleName>>,
  setShowNewRoleEdit: Dispatch<SetStateAction<boolean>>,
  toast: ReturnType<typeof useToast>,
  t: (key: string) => string
): void => {
  const isDuplicate = possibleRoles.some((existingRole) => existingRole.name.it === roleNames.it)
  if (!isDuplicate) {
    const newRole: CreateActivityRequestPossibleRolesInner = {
      name: roleNames as unknown as LocalizedName
    }
    setPossibleRoles((prevRoles) => [...prevRoles, newRole])
  } else {
    toast({
      title: t('duplicate_activities'),
      status: 'error',
      duration: 2000,
      isClosable: true
    })
  }
  setRoleNames({})
  setShowNewRoleEdit(false)
}

export const updatePossibleRoleName = (
  index: number,
  langCode: string,
  value: string,
  setPossibleRoles: Dispatch<SetStateAction<CreateActivityRequestPossibleRolesInner[]>>,
  setConfirmTranslateButtonDisabled: Dispatch<SetStateAction<boolean>>,
  possibleRoles: CreateActivityRequestPossibleRolesInner[],
  toast: ReturnType<typeof useToast>,
  t: (key: string) => string
): void => {
  setPossibleRoles((prevRoles) =>
    prevRoles.map((role, i) =>
      i === index
        ? {
            ...role,
            name: {
              ...role.name,
              [langCode]: value
            }
          }
        : role
    )
  )
  const isDuplicate = possibleRoles.some((role, indexPR) => indexPR !== index && role.name[langCode as keyof LocalizedName] === value)
  const selectedLang = languages.filter((val) => val.code === langCode)[0].name

  if (isDuplicate) {
    toast({
      title: t('duplicate_activities_translate') + t(selectedLang),
      status: 'error',
      duration: 2000,
      isClosable: true
    })
    setConfirmTranslateButtonDisabled(true)
  } else {
    setConfirmTranslateButtonDisabled(false)
  }
}

export const handleCurrentRoleChange = (
  langCode: string,
  value: string,
  showTranslateMenu: boolean,
  currentRoleValue: IRoleName,
  setCurrentRoleValue: Dispatch<SetStateAction<IRoleName>>
): void => {
  const allUpdated = updateAllLanguages(langCode, value, currentRoleValue, setCurrentRoleValue, showTranslateMenu)
  if (!allUpdated) {
    setCurrentRoleValue({ ...currentRoleValue, [langCode]: value })
  }
}

export const handleActivityNameChange = (
  langCode: string,
  value: string,
  showTranslateMenu: boolean,
  activityNames: IActivityName,
  setActivityNames: Dispatch<SetStateAction<IActivityName>>
): void => {
  const allUpdated = updateAllLanguages(langCode, value, activityNames, setActivityNames, showTranslateMenu)
  if (!allUpdated) {
    setActivityNames({ ...activityNames, [langCode]: value })
  }
}

export const handleCategoryNameChange = (
  langCode: string,
  value: string,
  showTranslateMenu: boolean,
  categoryNames: ICategoryName,
  setCategoryNames: Dispatch<SetStateAction<ICategoryName>>
): void => {
  const allUpdated = updateAllLanguages(langCode, value, categoryNames, setCategoryNames, showTranslateMenu)
  if (!allUpdated) {
    setCategoryNames({ ...categoryNames, [langCode]: value })
  }
}
